import React, { useCallback, useState } from 'react';

import { useIntl } from 'react-intl-next';

import { UI_EVENT_TYPE } from '@atlaskit/analytics-gas-types';
import { type CreateUIAnalyticsEvent, useAnalyticsEvents } from '@atlaskit/analytics-next';
import { IconButton } from '@atlaskit/button/new';
import AddIcon from '@atlaskit/icon/core/add';
import { ButtonItem, MenuGroup } from '@atlaskit/menu';
import { fg } from '@atlaskit/platform-feature-flags';
import { componentWithFG } from '@atlaskit/platform-feature-flags-react';
import { Popup } from '@atlaskit/popup';

import { ANALYTICS_CHANNEL } from '../../constants';
import type { GlobalShortcutActionsItem } from '../../types';

import { messages } from './messages';

type ShortcutActionProps = {
	actions: GlobalShortcutActionsItem[];
	analyticsName?: string;
};

type ButtonItemWithAnalyticsProps = {
	action: GlobalShortcutActionsItem;
	fireShortcutActionDropdownItemClickedAnalyticsEvent: (itemId?: string) => void;
	index: number,
	setIsOpen: (open: boolean) => void;
};

const ShortcutActionMenuDeprecated = ({ actions }: ShortcutActionProps) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isOpen, setIsOpen] = useState(false);

	const handleDropdownItemClick = useCallback((itemId?: string) => {
		fireShortcutActionDropdownItemClickedAnalyticsEvent(createAnalyticsEvent, itemId);
	}, [createAnalyticsEvent]);

	return (
		<Popup
			shouldRenderToParent
			isOpen={isOpen}
			onClose={() => setIsOpen(false)}
			placement="bottom-start"
			content={() => (
				<MenuGroup>
					{actions.map((action, index) => (
						<ButtonItemWithAnalytics
							index={index}
							action={action}
							fireShortcutActionDropdownItemClickedAnalyticsEvent={
								handleDropdownItemClick
							}
							setIsOpen={setIsOpen}
						/>
					))}
				</MenuGroup>
			)}
			trigger={(triggerProps) => (
				<IconButton
					{...triggerProps}
					appearance="subtle"
					spacing="compact"
					icon={(props) => <AddIcon {...props} size="small" />}
					label={formatMessage(messages.shortcutActionOpenMenuLabel)}
					onClick={() => setIsOpen(true)}
				/>
			)}
		/>
	);
};

const ShortcutActionMenuUpdated = ({ actions, analyticsName }: ShortcutActionProps) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isOpen, setIsOpen] = useState(false);

	const handleClick = useCallback(() => {
		setIsOpen(true);
		fireShortcutActionMenuClickedAnalyticsEvent(createAnalyticsEvent, analyticsName);
	}, [createAnalyticsEvent, analyticsName, setIsOpen]);

	const handleDropdownItemClick = useCallback((itemId?: string) => {
		fireShortcutActionDropdownItemClickedAnalyticsEvent(createAnalyticsEvent, itemId);
	}, [createAnalyticsEvent]);

	return (
		<Popup
			shouldRenderToParent
			isOpen={isOpen}
			onClose={() => setIsOpen(false)}
			placement="bottom-start"
			content={() => (
				<MenuGroup>
					{actions.map((action, index) => (
						<ButtonItemWithAnalytics
							index={index}
							action={action}
							fireShortcutActionDropdownItemClickedAnalyticsEvent={
								handleDropdownItemClick
							}
							setIsOpen={setIsOpen}
						/>
					))}
				</MenuGroup>
			)}
			trigger={(triggerProps) => (
				<IconButton
					{...triggerProps}
					appearance="subtle"
					spacing="compact"
					icon={(props) => <AddIcon {...props} size="small" />}
					label={formatMessage(messages.shortcutActionOpenMenuLabel)}
					onClick={handleClick}
				/>
			)}
		/>
	);
};

export const ShortcutActionMenu = componentWithFG(
	'global_shortcuts_add_actions_click_event',
	ShortcutActionMenuUpdated,
	ShortcutActionMenuDeprecated,
);

const ButtonItemWithAnalytics = ({
	index,
	action,
	fireShortcutActionDropdownItemClickedAnalyticsEvent,
	setIsOpen,
}: ButtonItemWithAnalyticsProps) => {
	const handleClick = useCallback(
		(e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
			e.preventDefault();
			action.onClick?.();
			if (fg('navx-global-side-nav-confluence')) {
				fireShortcutActionDropdownItemClickedAnalyticsEvent(action.itemId);
			}
			setIsOpen(false);
		},
		[action, fireShortcutActionDropdownItemClickedAnalyticsEvent, setIsOpen],
	);

	return <ButtonItem key={index} onClick={handleClick}>{action.component}</ButtonItem>;
};

const fireShortcutActionMenuClickedAnalyticsEvent = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	analyticsName?: string,
) => {
	createAnalyticsEvent({
		eventType: UI_EVENT_TYPE,
		action: 'clicked',
		actionSubject: 'button',
		actionSubjectId: 'ShortcutActionMenu',
		attributes: {
			name: analyticsName,
		},
	}).fire(ANALYTICS_CHANNEL);
};

const fireShortcutActionDropdownItemClickedAnalyticsEvent = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	itemId?: string,
) => {
	createAnalyticsEvent({
		eventType: UI_EVENT_TYPE,
		action: 'clicked',
		actionSubject: 'button',
		actionSubjectId: 'ShortcutActionDropdownItem',
		attributes: {
			itemId: itemId,
		},
	}).fire(ANALYTICS_CHANNEL);
};
